import React from 'react'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {hasPermission, parseId} from '../../../helpers'
import {
  SET_FORCE_PASSWORD_CHANGE,
  SET_USER_TWO_FACTOR_REQUIRED,
} from '../../../gql/mutations/userMutations'
import {useGetUser} from '../../../store/userStore'
import {ALL_PERMISSIONS} from '../../../helpers/consts'
import {TwoFactorSettingsPanel} from '../../../components/security/TwoFactorSettingsPanel'
import {PasskeySettingsPanel} from '../../../components/security/PasskeySettingsPanel'
import {IPasskeyCredential} from '../../../types/auth'

type ForcePasswordChangeCardProps = {
  userId: string
  forcePasswordChange?: boolean
  twoFactorEnabled?: boolean
  twoFactorRequired?: boolean
  twoFactorEmailFallbackEnabled?: boolean
  passkeyCredentials?: IPasskeyCredential[]
  onUpdated?: () => void
}

export const ForcePasswordChangeCard: React.FC<ForcePasswordChangeCardProps> = ({
  userId,
  forcePasswordChange = false,
  twoFactorEnabled = false,
  twoFactorRequired = false,
  twoFactorEmailFallbackEnabled = false,
  passkeyCredentials = [],
  onUpdated,
}) => {
  const [enabled, setEnabled] = React.useState<boolean>(forcePasswordChange)
  const [twoFactorRequiredEnabled, setTwoFactorRequiredEnabled] =
    React.useState<boolean>(twoFactorRequired)
  const [setForcePasswordChange, {loading: loadingForcePasswordChange}] =
    useMutation(SET_FORCE_PASSWORD_CHANGE)
  const [setUserTwoFactorRequired, {loading: loadingTwoFactorRequired}] = useMutation(
    SET_USER_TWO_FACTOR_REQUIRED
  )
  const currentUserId = useGetUser((s) => s.user?.user?.id)
  const permissions = useGetUser((s) => s.user?.permissions) || []
  const canResetTwoFactor = hasPermission(permissions, ALL_PERMISSIONS.EDIT_USER)
  const isEditingSelf = String(parseId(userId)) === String(parseId(currentUserId || 0))
  const loading = loadingForcePasswordChange || loadingTwoFactorRequired

  React.useEffect(() => {
    setEnabled(Boolean(forcePasswordChange))
  }, [forcePasswordChange])

  React.useEffect(() => {
    setTwoFactorRequiredEnabled(Boolean(twoFactorRequired))
  }, [twoFactorRequired])

  const handleSave = async () => {
    try {
      const parsedUserId = parseId(userId)
      const forcePasswordChanged = enabled !== Boolean(forcePasswordChange)
      const twoFactorRequiredChanged = twoFactorRequiredEnabled !== Boolean(twoFactorRequired)

      if (!forcePasswordChanged && !twoFactorRequiredChanged) {
        toast.info('No login security changes to save.')
        return
      }

      if (forcePasswordChanged) {
        const response = await setForcePasswordChange({
          variables: {
            userId: parsedUserId,
            enabled,
          },
        })

        const result = response.data?.setForcePasswordChangeMutation
        if (!result?.success) {
          const errorMessage = result?.errors?.[0]?.message || 'Unable to update this setting.'
          toast.error(errorMessage)
          return
        }
      }

      if (twoFactorRequiredChanged) {
        const response = await setUserTwoFactorRequired({
          variables: {
            userId: parsedUserId,
            enabled: twoFactorRequiredEnabled,
          },
        })

        const result = response.data?.setUserTwoFactorRequiredMutation
        if (!result?.success) {
          const errorMessage =
            result?.errors?.[0]?.message || 'Unable to update the 2FA requirement.'
          toast.error(errorMessage)
          return
        }
      }

      toast.success('Login security updated.')
      onUpdated?.()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      toast.error(`Error updating setting: ${message}`)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Login Security</h3>
        </div>
      </div>

      <div className='card-body border-top p-9'>
        <div className='mb-8'>
          <TwoFactorSettingsPanel
            canManageOwnTwoFactor={isEditingSelf}
            canResetTwoFactor={canResetTwoFactor}
            showAdminRequiredControl={false}
            targetUserId={parseId(userId)}
            twoFactorEnabled={twoFactorEnabled}
            twoFactorRequired={twoFactorRequired}
            twoFactorEmailFallbackEnabled={twoFactorEmailFallbackEnabled}
            onUpdated={onUpdated}
          />
        </div>

        <div className='separator separator-dashed my-8'></div>

        <div className='mb-8'>
          <PasskeySettingsPanel
            credentials={passkeyCredentials}
            canManageOwnPasskeys={isEditingSelf}
            canManageUserPasskeys={canResetTwoFactor && !isEditingSelf}
            targetUserId={parseId(userId)}
            onUpdated={onUpdated}
          />
        </div>

        <div className='separator separator-dashed my-8'></div>

        {canResetTwoFactor && (
          <>
            <div className='form-check form-switch form-check-custom form-check-solid mb-6'>
              <input
                className='form-check-input'
                type='checkbox'
                id='two_factor_required_user'
                checked={twoFactorRequiredEnabled}
                onChange={(e) => setTwoFactorRequiredEnabled(e.target.checked)}
              />
              <label className='form-check-label fw-bold' htmlFor='two_factor_required_user'>
                Require 2FA setup
              </label>
            </div>

            <div className='text-gray-600 fs-7 mb-8'>
              {twoFactorEnabled
                ? 'If enabled, this user will not be able to disable 2FA.'
                : 'If enabled, the user will be redirected to profile and must configure 2FA on the next login.'}
            </div>
          </>
        )}

        <div className='form-check form-switch form-check-custom form-check-solid mb-6'>
          <input
            className='form-check-input'
            type='checkbox'
            id='force_password_change_next_login'
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label className='form-check-label fw-bold' htmlFor='force_password_change_next_login'>
            Force password change on next login
          </label>
        </div>

        <div className='text-gray-600 fs-7'>
          If enabled, this user must change their password before accessing the system.
        </div>
      </div>

      <div className='card-footer d-flex justify-content-end py-6 px-9'>
        <button type='button' className='btn btn-primary' onClick={handleSave} disabled={loading}>
          {!loading && 'Save'}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </div>
  )
}
